import * as React from 'react'
import { createPortal } from 'react-dom'
import { FaIcon, faCalendar } from '@/lib/fa'
import {
  romanMonthKeys,
  monthKeysShort,
} from '@/modules/shared/constants/months'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import MonthMultiPickerTimesheet from '../date/MonthMultiPickerTimesheet'

interface MonthPickerButtonProps {
  useRomanNumerals?: boolean
  onChange?: (selectedMonths: string[]) => void
  initialSelectedMonths?: string[]
  companyId: string
}

const MonthMultiPickerButton: React.FC<MonthPickerButtonProps> = ({
  useRomanNumerals = false,
  onChange,
  initialSelectedMonths = [],
  companyId,
}) => {
  const [open, setOpen] = React.useState(false)

  const { t, tSafe } = useLocalize()
  const placeholder = tSafe(
    'timesheet.select-period-to-calculate',
    'Wybierz okres do przeliczenia',
  )

  const [selectedMonths, setSelectedMonths] = React.useState<string[]>(
    initialSelectedMonths,
  )

  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left })
    }
    setOpen(willOpen)
  }

  React.useLayoutEffect(() => {
    if (!open || !buttonRef.current || !dropdownRef.current) return

    const margin = 8
    const rect = buttonRef.current.getBoundingClientRect()
    const dropdownWidth = dropdownRef.current.offsetWidth
    const dropdownHeight = dropdownRef.current.offsetHeight
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = rect.left
    if (left + dropdownWidth > viewportWidth - margin) {
      left = rect.right - dropdownWidth
    }
    if (left < margin) {
      left = margin
    }

    let top = rect.bottom + 4
    if (top + dropdownHeight > viewportHeight - margin) {
      const above = rect.top - dropdownHeight - 4
      if (above > margin) {
        top = above
      }
    }

    setDropdownPosition((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left },
    )
  }, [open])

  const handleConfirm = (months: string[]) => {
    setSelectedMonths(months)
    if (onChange) {
      onChange(months)
    }
    setOpen(false)
  }

  const displayText = React.useMemo(() => {
    if (!selectedMonths || selectedMonths.length === 0) return placeholder

    const parseMonth = (value: string) => {
      const parts = value.split('-')
      if (parts.length !== 2) return null
      const year = parseInt(parts[0], 10)
      const monthNum = parseInt(parts[1], 10)
      if (isNaN(year) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
        return null
      }
      return { year, monthNum }
    }

    if (selectedMonths.length === 1) {
      const parsed = parseMonth(selectedMonths[0])
      if (!parsed) return placeholder

      return useRomanNumerals
        ? `${parsed.year} ${romanMonthKeys[parsed.monthNum - 1]}`
        : `${parsed.year}-${String(parsed.monthNum).padStart(2, '0')}`
    }

    const monthNames = selectedMonths.flatMap((monthStr) => {
      const parsed = parseMonth(monthStr)
      return parsed ? [t(monthKeysShort[parsed.monthNum - 1])] : []
    })

    const displayNames = monthNames.slice(0, 3).join(', ')
    const finalText = monthNames.length > 3 ? `${displayNames}...` : displayNames

    return finalText || placeholder
  }, [selectedMonths, useRomanNumerals, t, placeholder])

  return (
    <div className="tw-relative tw-z-50 tw-inline-block">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-bg-transparent hover:tw-underline hover:tw-text-primary tw-px-2 tw-py-1`}
      >
        <FaIcon
          icon={faCalendar}
          className="tw-h-4 tw-w-4 tw-text-[#1f2937]"
          style={{ fontWeight: 900, fontSize: '12px' }}
        />
        <p className="tw-text-xs tw-text-[#1f2937] hover:tw-text-primary">
          {displayText}
        </p>
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[200]"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <MonthMultiPickerTimesheet
              selectedMonths={selectedMonths}
              onConfirm={handleConfirm}
              companyId={companyId}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export default MonthMultiPickerButton
